import * as React from "react";
import * as ReactDOM from "react-dom";
import { CurrencyInput, Input as BaseInput, InputAlign, MaskedInput } from "react-ui";
import { InputProps as BaseInputProps } from "@skbkontur/react-ui";

import { AbsoluteResolvedModelPath } from "../../../../../Common/ModelPath/ModelPath";
import { toHtmlElementOrNull } from "../../Commons/DOM/DomHelpers";
import { useScrollTo } from "../../Internals/Contexts/ScrollToContext";
import { MetricsValueSenderActions } from "../../Internals/Metrics/MetricsValueSender";
import { TextOverflowHint } from "../../Internals/TextOverflowHint/TextOverflowHint";
import { themed } from "../../Internals/ThemeProvider/ThemedWorkaround";
import { ControlTooltipProps } from "../../Internals/Tooltip/ControlTooltipProps";
import { Tooltip } from "../../Internals/Tooltip/Tooltip";
import { DataPasteContext, DatePasteStatus } from "../../MultiControls/MultiLine/TableDataPasteContext";
import { FocusManagementObserver } from "../../Services/FocusManagement/FocusManagementObserver";
import { useTestPageObject } from "../../Services/TestPageObjects/PageObjectExposure";
import { isScreenshotTestingMode } from "../../Testing";
import { emptyRef } from "../../TypingUtils";
import { ControlValidationProps } from "../../Validations/ValidationProps";
import { Text } from "../../ValueViewers/Text/Text";
import { cutExtraSpacesFromPaste, cutExtraSpaces } from "../../../Helpers/CutExtraSpaces";
import { CurrencyHelper } from "../../Internals/ControlsReexport/CustomControls/CurrencyInput/CurrencyHelper";
import { isNullOrEmpty } from "../../../../../Common/TypingUtils";
import { CurrencyInputProps } from "../../Internals/ControlsReexport/CustomControls/CurrencyInput/CurrencyInput";
import { HighlightingOptions } from "../../ExternalHighlighting";
import { tryScrollIntoViewWithFallback } from "../../../Helpers/BrowserHelpers";

import {
    fixDecimalComma,
    fixDecimalValue,
    fixNumberValue,
    formatValue,
    negativeValueToBrackets,
    wrapWithLimitedByRegexp,
} from "./InputHelpers";
import { GrayWrapper, hideCursor, highlighted, ReadOnlyAlignInput, ReadOnlyTextWrapper } from "./Input.styles";
import { inputTestAdapter } from "./InputTestPageObject";
import { renderPrefix, renderSuffix } from "./RenderAffix";

export type InputValueType = "number" | "decimal";

export interface InputCommonProps {
    value: undefined | string;
    isLink?: boolean;
    isReadOnly?: boolean;
    onClick?: () => void;
    bindingPath: AbsoluteResolvedModelPath;
    suffix?: React.ReactNode;
    prefix?: React.ReactNode;
    "data-tid"?: string;
    currency?: boolean;
    currencyIntegerDigits?: number;
    currencyFractionDigits?: number;
    type?: InputValueType;
    withIgnoreAutoFlag?: boolean;
    ignoreAutoFlag?: boolean;
    setIgnoreAutoFlag: (value: boolean) => void;
    dataType?: InputValueType;
    align?: InputAlign;
    noReadonlyPadding?: boolean;
    removeReadonlyBackground?: boolean;
    focusByEnterOnNextControl: (event: React.KeyboardEvent) => void;
    disabled?: boolean;
    showNegativeAsBrackets?: boolean;
    inputLimit?: string;
    isTooltipEnabled?: boolean;
    metrics: MetricsValueSenderActions;
    highlighted?: boolean;
    width: string | number;
    externalHighlighted?: boolean;
    externalFocused?: boolean;
    externalHighlightingOptions?: HighlightingOptions;
}

export interface InputProps extends ControlTooltipProps, ControlValidationProps, InputCommonProps {
    onChange: (value: string | undefined) => void;
    onChangeWithHelper: (value: string | undefined) => void;
    onBlur: () => void;
    onInnerValueChange: (value: string | undefined) => void;
    // Used in MultilineField
    autoFocus?: boolean;
    formatChars?: Record<string, string>;
    nullableMask?: string;
    mask?: string;
    maskChar?: string;
    rejectInvalidByMaskValue?: boolean;
    navigationPath?: string;
    placeholder?: string;
    sendMetrics?: boolean;
    onMouseEnter?: (event: React.MouseEvent<HTMLLabelElement>) => void;
    onMouseLeave?: (event: React.MouseEvent<HTMLLabelElement>) => void;
    ignoreMaskCharsInValue?: boolean;
}

export function Input(props: InputProps): JSX.Element {
    const baseInput = React.useRef<BaseInput>(emptyRef);
    const currencyInput = React.useRef<CurrencyInput>(emptyRef);
    const readOnlyTextWrapper = React.useRef<HTMLDivElement>(emptyRef);
    const scrollTo = useScrollTo();
    const processTableDataPaste = React.useContext(DataPasteContext);
    const mask = React.useRef<string | undefined>(props.nullableMask);
    const highlightingOptions = props.externalHighlightingOptions;
    const [isTooltipOpened, setIsTooltipOpened] = React.useState(false);

    React.useEffect(() => {
        props.onInnerValueChange(props.value);
    }, [props.value]);

    const inputElement = React.useMemo(
        () => toHtmlElementOrNull(ReactDOM.findDOMNode(currencyInput.current ?? baseInput.current)) ?? undefined,
        [currencyInput.current ?? baseInput.current]
    );

    const inputComponentKey = React.useMemo(
        () => props["data-tid"] ?? `input_${props.bindingPath.getHash()}`,
        [props["data-tid"], props.bindingPath]
    );

    const blink = () => {
        baseInput.current?.blink();
    };

    const onChangeWithLimit: (value: undefined | string, prevValue: undefined | string) => void = React.useMemo(
        () => wrapWithLimitedByRegexp(props.onChange, props.inputLimit, blink),
        [props.inputLimit]
    );

    const onChangeWithLimitAndHelper: (value: undefined | string, prevValue: undefined | string) => void =
        React.useMemo(
            () => wrapWithLimitedByRegexp(props.onChangeWithHelper, props.inputLimit, blink),
            [props.inputLimit]
        );

    React.useEffect(() => {
        if (props.withIgnoreAutoFlag && !props.ignoreAutoFlag) {
            props.setIgnoreAutoFlag(true);
        }
    }, []);

    React.useEffect(() => {
        if (highlightingOptions?.isTooltipFixed !== undefined) {
            setIsTooltipOpened(highlightingOptions?.isTooltipFixed);
        }
    }, [highlightingOptions?.isTooltipFixed]);

    React.useEffect(() => {
        if (!props.disabled) {
            const type = props.type || props.dataType;
            if (type === "decimal") {
                const fixedValue = fixDecimalValue(props.value);

                if (props.value !== fixedValue && fixedValue != undefined) {
                    onChangeWithLimit(fixedValue, props.value);
                }
            }
        }
    }, [props.disabled]);

    const handlePaste = (event: React.ClipboardEvent<unknown>): DatePasteStatus => {
        if (props.disabled) {
            return DatePasteStatus.Failure;
        }

        mask.current = props.rejectInvalidByMaskValue ? props.mask : undefined;

        const result = processTableDataPaste(event.clipboardData, {
            bindingPath: props.bindingPath,
        });
        if (props.currency) {
            return result;
        }
        if (result == DatePasteStatus.Failure) {
            const type = props.type || props.dataType;
            const formattedText = cutExtraSpacesFromPaste({
                event: event,
                previousText: props.value,
                removeNewLines: true,
            });
            if (type === "decimal") {
                props.onChangeWithHelper(fixDecimalComma(formattedText));
            } else {
                props.onChangeWithHelper(formattedText);
            }
        }
        event.preventDefault();

        return result;
    };

    const valueBecameValidByMask = !mask.current && props.nullableMask == props.mask;

    const handleChange = (value: undefined | null | string): void => {
        const type = props.type || props.dataType;

        mask.current = props.rejectInvalidByMaskValue ? props.mask : valueBecameValidByMask ? props.mask : mask.current;

        if (value != undefined) {
            if (type === "decimal") {
                onChangeWithLimitAndHelper(fixDecimalComma(value), props.value);
            } else {
                onChangeWithLimitAndHelper(value, props.value);
            }
        } else {
            if (props.currency) {
                props.onChangeWithHelper("");
            }
            onChangeWithLimitAndHelper(undefined, props.value);
        }
    };

    const handleBlur = (): void => {
        const type = props.type || props.dataType;
        const formattedText = cutExtraSpaces(props.value);

        if (type === "number") {
            onChangeWithLimit(fixNumberValue(formattedText), formattedText);
        } else if (type === "decimal") {
            onChangeWithLimit(fixDecimalValue(formattedText), formattedText);
        } else {
            props.onChange(formattedText);
        }
        props.onBlur();
        props.metrics?.sendValue(props.bindingPath, formattedText);
    };

    const handleFocus = (): void => {
        props.metrics?.saveValue(props.value);
    };

    const handleTooltipClosed = (): void => {
        setIsTooltipOpened(false);
    };

    const handleRequestFocus = (): void => {
        if (inputElement != undefined) {
            scrollTo(inputElement);
        }
        (currencyInput.current ?? baseInput.current)?.focus();
        (currencyInput.current ?? baseInput.current)?.selectAll();
        if (props.isReadOnly && readOnlyTextWrapper.current) {
            tryScrollIntoViewWithFallback(readOnlyTextWrapper.current, {
                block: "center",
                inline: "center",
                behavior: "smooth",
            });
        }
    };

    useTestPageObject(
        () => inputTestAdapter(props.value, inputElement, handleChange),
        [inputElement, props.value, handleChange]
    );

    const { error, warning, isTooltipEnabled, tooltip, isReadOnly, externalHighlighted } = props;
    const tooltipValue = error ?? warning ?? tooltip;
    const highlightingText = highlightingOptions?.tooltipText;
    const tooltipEnabledInReadOnly = isReadOnly && externalHighlighted && highlightingText;
    const tooltipTrigger =
        !isReadOnly && isTooltipEnabled && tooltipValue
            ? "hover&focus"
            : tooltipEnabledInReadOnly
              ? isTooltipOpened
                  ? "opened"
                  : "hover&focus"
              : "closed";
    const hintEnabled = isTooltipEnabled && tooltipTrigger === "closed";

    const inputComponentProps: InputComponentProps = {
        ...props,
        type: props.type || props.dataType,
        nullableMask: mask.current ?? props.nullableMask,
        baseInput: baseInput,
        currencyInput: currencyInput,
        handleFocus: handleFocus,
        handleBlur: handleBlur,
        handleChange: handleChange,
        handlePaste: handlePaste,
    };

    return (
        <Tooltip
            trigger={tooltipTrigger}
            value={tooltipValue}
            pos={highlightingText ? "right middle" : props.tooltipPosition}
            width={props.tooltipWidth}
            closeButton={isTooltipOpened}
            onCloseClick={handleTooltipClosed}
            highlightingTooltipText={highlightingText}>
            <FocusManagementObserver onRequestFocus={handleRequestFocus}>
                <TextOverflowHint
                    useChildDomElementValue
                    showHint={hintEnabled}
                    text={
                        props.currency
                            ? CurrencyHelper.format(props.value, {
                                  fractionDigits: props.currencyFractionDigits,
                                  hideTrailingZeros: false,
                              })
                            : (props.value ?? "")
                    }>
                    <InputComponent
                        {...inputComponentProps}
                        readOnlyRef={readOnlyTextWrapper}
                        key={inputComponentKey}
                    />
                </TextOverflowHint>
            </FocusManagementObserver>
        </Tooltip>
    );
}

export const ReadOnlyInputText = (props: InputCommonProps): JSX.Element => {
    let value = props.value;
    if (!isNullOrEmpty(props.value)) {
        value = formatValue(props);
    }
    if (props.showNegativeAsBrackets && !isNullOrEmpty(value)) {
        value = negativeValueToBrackets(value);
    }
    return (
        <Text
            value={value}
            bindingPath={props.bindingPath}
            data-tid={props["data-tid"]}
            onClick={props.onClick}
            isLink={props.isLink}
            currency={props.currency ?? false}
            currencyFractionDigits={props.currencyFractionDigits ? props.currencyFractionDigits : 0}
        />
    );
};

interface InputComponentProps extends InputProps {
    baseInput: React.RefObject<BaseInput>;
    currencyInput: React.RefObject<CurrencyInput>;
    handleFocus: () => void;
    handleBlur: () => void;
    handleChange: (value: undefined | null | string) => void;
    handlePaste: (event: React.ClipboardEvent<unknown>) => DatePasteStatus;
    baseInputAutoFocus?: boolean;
    readOnlyRef?: React.RefObject<HTMLDivElement>;
}

const InputComponent = (props: InputComponentProps): JSX.Element => {
    const [isFocused, setIsFocused] = React.useState(false);
    const highlightingType = props.externalHighlightingOptions
        ? props.externalHighlightingOptions.highlightingType
        : undefined;

    const handleFocus = () => {
        setIsFocused(true);
        props.handleFocus?.();
    };
    const handleBlur = () => {
        setIsFocused(false);
        props.handleBlur?.();
    };

    if (props.isReadOnly) {
        return (
            <ReadOnlyTextWrapper
                width={props.width}
                removeBackground={props.removeReadonlyBackground}
                noPadding={props.noReadonlyPadding}
                highlightingType={highlightingType}
                ref={props.readOnlyRef}>
                {props.prefix === "(" && <GrayWrapper>{renderPrefix(props.prefix)}</GrayWrapper>}
                <ReadOnlyAlignInput align={props.align}>
                    <ReadOnlyInputText {...props} />
                </ReadOnlyAlignInput>
                {props.suffix && <GrayWrapper>{renderSuffix(props.suffix)}</GrayWrapper>}
            </ReadOnlyTextWrapper>
        );
    }

    const commonInputProps: InputCommonProps | Omit<CurrencyInputProps, "hideTrailingZeros" | "value"> = {
        css: themed([props.highlighted && highlighted, isScreenshotTestingMode() && hideCursor]),
        align: props.align,
        disabled: props.disabled,
        onClick: props.onClick,
        error: Boolean(props.error),
        type: "text",
        "data-tid": props["data-tid"],
        placeholder: props.placeholder,
        warning: Boolean(props.warning),
        onValueChange: props.handleChange,
        onKeyDown: props.focusByEnterOnNextControl,
        onMouseEnter: props.onMouseEnter,
        onMouseLeave: props.onMouseLeave,
        suffix: renderSuffix(props.suffix),
        prefix: props.prefix,
    };

    if (props.currency) {
        return (
            <CurrencyInput
                {...(commonInputProps as CurrencyInputProps)}
                signed
                ref={props.currencyInput}
                integerDigits={props.currencyIntegerDigits}
                fractionDigits={props.currencyFractionDigits}
                autoFocus={props.autoFocus}
                value={props.value}
                onBlur={props.handleBlur}
                onPaste={props.handlePaste}
                onFocus={props.handleFocus}
                width={props.width ?? "100%"}
                selectAllOnFocus={true}
                showNegativeAsBrackets={props.showNegativeAsBrackets}
            />
        );
    }

    let value = formatValue(props);
    if (!isFocused && props.showNegativeAsBrackets && !isNullOrEmpty(value)) {
        value = negativeValueToBrackets(value);
    }

    const inputProps: BaseInputProps = {
        ...commonInputProps,
        onPaste: props.rejectInvalidByMaskValue ? undefined : props.handlePaste,
        value: value,
        onBlur: handleBlur,
        onFocus: handleFocus,
        width: props.width,
        selectAllOnFocus: props.type === "number" || props.type === "decimal",
        autoFocus: props.baseInputAutoFocus,
    };

    if (props.nullableMask) {
        const maskedInputOnChange = (val: string) => {
            if (props.ignoreMaskCharsInValue !== false) {
                // @ts-ignore
                const maskedInput = props.baseInput.current?.input;
                if (!maskedInput) {
                    throw new Error("maskedInput not detected");
                }

                // костыль для получения значения без maskChar, обещали что-нибудь сделать с этим https://chat.skbkontur.ru/kontur/pl/8wnd37kp73g1tbfb5trf5knafy
                const valueWithoutMaskChars: string | null = maskedInput.getAttribute("data-typed-value");

                commonInputProps.onValueChange(valueWithoutMaskChars);

                return;
            }

            commonInputProps.onValueChange(val);
        };

        return (
            <MaskedInput
                {...inputProps}
                ref={props.baseInput}
                value={value}
                error={Boolean(props.error)}
                warning={Boolean(props.warning)}
                type={"text"}
                // @ts-ignore
                mask={props.nullableMask}
                maskChar={props.maskChar}
                formatChars={props.formatChars}
                onValueChange={maskedInputOnChange}
            />
        );
    }

    return <BaseInput ref={props.baseInput} {...inputProps} />;
};
